import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactSortable } from "react-sortablejs";
import { FormikErrors } from "formik";
import { Col, Input, Label } from "reactstrap";
import ModalConfirm from "./ModalConfirm";
import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import { toast } from "react-toastify";
import DropdownStatusNew from "./DropdownStatusNew";
import UploadSource from "./UploadSource";

interface ResourceFormArrayProps {
  formik: any;
}

const PARAM_DEFAULT = {
  id: 1,
  type: "",
  label: "",
  input_type: "FILE",
  source_url: "",
};

const TYPE_OPTIONS = [
  { value: "PREPARATION_MATERIAL", label: "Preparation" },
  { value: "GUIDE_MATERIAL", label: "Guide" },
];

const INPUT_TYPE_OPTIONS = [
  { value: "LINK", label: "Link" },
  { value: "FILE", label: "File" },
];

const ResourceFormArray = ({ formik = "" }: ResourceFormArrayProps) => {
  const { t } = useTranslation();
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false);
  const [indexDelete, setIndexDelete] = useState<any>(null);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);

  const handleChangeSort = (data: any[]) => {
    const formatData = data?.map((item, index) => ({
      type: item?.type,
      label: item?.label,
      input_type: item?.input_type,
      source_url: item?.source_url,
      id: item?.id,
    }));

    formik.setFieldValue("resource", formatData);
  };

  const onCloseConfirmClick = () => {
    setIsOpenConfirm(false);
    setIndexDelete(null);
    setIsConfirmLoading((_prev) => false);
  };

  const handleAddItem = () => {
    formik.setFieldValue("resource", [
      ...formik?.values?.resource,
      {
        ...PARAM_DEFAULT,
        id: formik?.values?.resource?.length + 1,
        ordering: formik?.values?.resource?.length + 1,
      },
    ]);
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = formik?.values?.resource.filter(
      (_: any, i: any) => i !== index
    );
    formik.setFieldValue("resource", updatedItems);
  };

  const handleActionDelete = async () => {
    try {
      handleRemoveItem(indexDelete);
      setIsOpenConfirm(false);
      toast(
        `${t("The process has been completed.")}`,
        CONFIG_OPTION_TOAST_NORMAL
      );
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      setIndexDelete(null);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
    setIsConfirmLoading((_prev) => false);
  };

  return (
    <React.Fragment>
      <>
        <Col lg={12}>
          {/* <Label htmlFor="titleKoreaInput" className="form-label">
            {t("Resource")} <span className="text-danger"></span>
          </Label> */}

          <table className="w-100">
            <thead>
              <tr style={{ background: "#EDEDED", height: "37px" }}>
                <th style={{ minWidth: 25, width: 40 }}></th>
                <th style={{ width: 200 }}>{t("Type")}</th>
                <th style={{ width: 200 }}>{t("Label")}</th>
                <th style={{ width: 200 }}>{t("Input Type")}</th>
                <th>{t("Source Url")}</th>
                <th>{t("")}</th>
              </tr>
            </thead>
            <ReactSortable
              animation={200}
              delay={2}
              tag={"tbody"}
              filter={".locked_item"}
              preventOnFilter={false}
              list={formik?.values?.resource as any[]}
              setList={handleChangeSort}
              onMove={(evt) => !evt.related.classList.contains("locked_item")}
              handle={".my-handle"}
            >
              {formik?.values?.resource?.map((item: any, index: number) => {
                return (
                  <tr key={`${item?.id}`}>
                    <td
                      className="my-handle text-center"
                      style={{
                        paddingRight: "10px",
                        paddingTop: "22px",
                        paddingBottom: "5px",
                        verticalAlign: "top",
                      }}
                    >
                      {" "}
                      <i className="ri-menu-line"></i>
                    </td>
                    <td
                      style={{
                        paddingRight: "10px",
                        paddingTop: "5px",
                        paddingBottom: "5px",
                        verticalAlign: "top",
                      }}
                    >
                      <DropdownStatusNew
                        name="type"
                        dataList={TYPE_OPTIONS}
                        placeholder={`${t("Select type")}...`}
                        className="dropdown-status-rounded p-0"
                        classNamePrefix="name-prefix"
                        initialValue={item?.type}
                        onChangeSelect={(event: any) =>
                          formik.setFieldValue(
                            `resource.${index}.type`,
                            event?.value
                          )
                        }
                        // colors={COLOR_BAR_LIST_OPEN_STATUS}
                      />

                      {formik.touched.resource?.[index]?.type &&
                      formik.errors.resource &&
                      (formik.errors.resource[index] as FormikErrors<any>)
                        ?.type ? (
                        <small className="text-danger mt-2">
                          {(formik.errors.resource[index] as any)?.type}
                        </small>
                      ) : null}
                    </td>
                    <td
                      style={{
                        paddingRight: "10px",
                        paddingTop: "5px",
                        paddingBottom: "5px",
                        verticalAlign: "top",
                      }}
                    >
                      <Input
                        name={`resource.${index}.label`}
                        type="text"
                        className="form-control"
                        placeholder={t("Enter the label")}
                        max={16}
                        value={item?.label}
                        onBlur={(event) => {
                          formik.handleBlur(event);
                        }}
                        onChange={(event) => {
                          formik.setFieldValue(
                            `resource.${index}.label`,
                            event?.target?.value
                          );
                        }}
                      />
                      {formik.touched.resource?.[index]?.label &&
                      formik.errors.resource &&
                      (formik.errors.resource[index] as FormikErrors<any>)
                        ?.label ? (
                        <small className="text-danger mt-2">
                          {(formik.errors.resource[index] as any)?.label}
                        </small>
                      ) : null}
                    </td>
                    <td
                      style={{
                        paddingRight: "10px",
                        paddingTop: "5px",
                        paddingBottom: "5px",
                        verticalAlign: "top",
                      }}
                    >
                      <DropdownStatusNew
                        name="input_type"
                        dataList={INPUT_TYPE_OPTIONS}
                        placeholder={`${t("Select input type")}...`}
                        className="dropdown-status-rounded p-0"
                        classNamePrefix="name-prefix"
                        initialValue={item?.input_type}
                        onChangeSelect={(event: any) => {
                          if (event?.value !== item?.input_type) {
                            formik.setFieldValue(
                              `resource.${index}.source_url`,
                              ""
                            );
                          }
                          formik.setFieldValue(
                            `resource.${index}.input_type`,
                            event?.value
                          );
                        }}
                        // colors={COLOR_BAR_LIST_OPEN_STATUS}
                      />

                      {formik.touched.resource?.[index]?.input_type &&
                      formik.errors.resource &&
                      (formik.errors.resource[index] as FormikErrors<any>)
                        ?.input_type ? (
                        <small className="text-danger mt-2">
                          {(formik.errors.resource[index] as any)?.input_type}
                        </small>
                      ) : null}
                    </td>
                    <td
                      style={{
                        paddingRight: "10px",
                        paddingTop: "5px",
                        paddingBottom: "5px",
                        verticalAlign: "top",
                      }}
                    >
                      {item?.input_type === "FILE" ? (
                        <div className="">
                          <UploadSource
                            data={item?.source_url ? [item?.source_url] : []}
                            name={`resource.${index}.source_url`}
                            onLoad={(event: any, i: any) => {
                              formik.setFieldValue(
                                `resource.${index}.source_url`,
                                event?.[0]?.fullPath
                              );
                            }}
                          />
                        </div>
                      ) : (
                        <Input
                          name={`resource.${index}.source_url`}
                          type="text"
                          className="form-control"
                          placeholder={t("Enter the url")}
                          max={16}
                          value={item?.source_url}
                          onBlur={(event) => {
                            formik.handleBlur(event);
                          }}
                          onChange={(event) => {
                            formik.setFieldValue(
                              `resource.${index}.source_url`,
                              event?.target?.value
                            );
                          }}
                        />
                      )}
                      {formik.touched.resource?.[index]?.source_url &&
                      formik.errors.resource &&
                      (formik.errors.resource[index] as FormikErrors<any>)
                        ?.source_url ? (
                        <small className="text-danger mt-2">
                          {(formik.errors.resource[index] as any)?.source_url}
                        </small>
                      ) : null}
                    </td>
                    {/* <td style={{ verticalAlign: "baseline" }}>
                      <textarea
                        className="form-control"
                        name={`resource.${index}.description`}
                        value={item?.description}
                        placeholder={`${t("Enter the description")}...`}
                        onChange={(event) => {
                          formik.setFieldValue(
                            `resource.${index}.description`,
                            event?.target?.value
                          );
                        }}
                        style={{ height: 40 }}
                      />
                    </td> */}
                    <td style={{ verticalAlign: "top" }}>
                      <div
                        className="d-flex pt-2"
                        style={{ justifyContent: "center" }}
                      >
                        <div>
                          {
                            <div
                              className="text-danger cursor-pointer"
                              onClick={() => {
                                setIsOpenConfirm(true);
                                setIndexDelete(index);
                              }}
                            >
                              <i className="ri-delete-bin-3-fill align-bottom"></i>
                            </div>
                          }
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </ReactSortable>
          </table>
        </Col>
        {formik.touched?.resource && formik.errors?.resource ? (
          <small className="text-danger mt-2">
            {typeof formik.errors?.resource === "string" &&
              formik.errors?.resource}
          </small>
        ) : null}
        <Col lg={12} className="mb-2">
          <div
            className="text-primary text-center cursor-pointer mt-3 d-flex align-items-center justify-content-center gap-2"
            style={{ fontSize: 30, background: "#0bcee91c" }}
            onClick={handleAddItem}
          >
            + <span style={{ fontSize: 14 }}>{t("Add Resource")}</span>
          </div>
        </Col>
      </>

      <ModalConfirm
        header={t("Delete this resource")}
        isOpen={isOpenConfirm}
        isLoading={isConfirmLoading}
        onClose={onCloseConfirmClick}
        onConfirm={handleActionDelete}
      />
    </React.Fragment>
  );
};

export default ResourceFormArray;
